import { DEFAULT_ERROR, post } from '../../../../DAL/data-provider';
import { formatBridgerPayError, redirectToThankYouPage } from './bridger-pay-provider';
import { getHiddenDataString } from '../../../../domain/hidden-data-provider';

declare global {
    interface Window {
        BRIDGERPAY_CONFIG: any;
    }
}


export const FREE_TRIAL_ABUSE_ERROR_CODE = -5;
export const SUBSCRIPTION_ADD_PAYMENT_TOKEN_WARNING = 8;

export interface IApplePayData {
    cashierKey: string;
    cashierToken: string;
    orderGUID: string;
    isInAppPurchase: boolean;
    orderRnd?: string;
}

export enum ApplePayReferrer {
    Cart = 0,
    Omc = 1,
    ChangePaymentMethod = 3,
}

export const getCompleteApplePayUrlByReferrer = (referrer: ApplePayReferrer): string => {
    switch (referrer) {
        case ApplePayReferrer.Omc: {
            return '/api/bridgerpay/complete-applepay-order-omc';
        }
        case ApplePayReferrer.ChangePaymentMethod: {
            return '/api/bridgerpay/complete-applepay-order-change-payment-method';
        }
        default: {
            return '/api/bridgerpay/complete-applepay-order';
        }
    }
};

export const sendApplePayEventToGA = (action: string, label: string) => {
    if(!window.dataLayer) return;

    window.dataLayer.push({
        'GA_event_action': action
        , 'GA_event_category': window.location.pathname
        , 'GA_event_label': label
        , 'event': 'GTM event To GA'
    });
};

/**
 * OLD - not used!!! ----------
 * https://developers.bridgerpay.com/docs/apple-pay#use-front-end-events-to-ensure-a-seamless-apple-pay-experience
 * Events: https://developers.bridgerpay.com/docs/event-capture#events-descriptions
 */
export const handleApplePayLauncher = (
    cashierKey: string,
    cashierToken: string,
    orderGUID: string,
    isInAppPurchase: boolean,
    setServerError: Function,
    setLoading: Function,
) => {
    // Hide the BridgerPay’s default wallet button (client-side)
    window.BRIDGERPAY_CONFIG = {
        hideWallets: ['ApplePay'],
    };

    const releaseResources = () => {
        document.getElementById('bridgerpay-script')?.remove();

        window.removeEventListener(`[bp][checkout:${ cashierKey }]:wallet-button-is-ready`, walletButtonIsReady, false);
        window.removeEventListener(`[bp][checkout:${ cashierKey }]:wallet-cancelled`, walletCanceled, false);
        window.removeEventListener(`[bp][checkout:${ cashierKey }]:wallet-result`, walletResult, false);
        window.removeEventListener(`[bp][checkout:${ cashierKey }]:wallet-processing-payment`, walletProcessingPayment, false);
        window.removeEventListener(`[bp][checkout:${ cashierKey }]:no-wallet-provider-found`, noWalletProviderFound, false);
        window.removeEventListener(`[bp][checkout:${ cashierKey }]:wallet-provider-cannot-be-used`, walletProviderCannotBeUsed, false);
    };

    const walletButtonIsReady = (evt: Event) => {
        const result = (evt as CustomEvent).detail;

        console.log('wallet-button-is-ready', result);

        /**
         * Trigger Apple Pay overlay
         * https://developers.bridgerpay.com/docs/apple-pay#6-trigger-apple-pay-overlay-client-side
         * This event triggers the same Apple Pay session as the default BridgerPay button,
         * fully integrated with your checkout session.
         */
        window.dispatchEvent(
            new CustomEvent(`[bp][checkout:${ cashierKey }]:wallet-open-applepay-overlay`),
        );
    };

    /**
     * If the user cancels the payment process, a "cancelled" event will be triggered.
     * You should handle this event accordingly.
     * Happens, for example, if the user clicks in any place in iphone
     * except the "right button".
     */
    const walletCanceled = (evt: Event) => {
        const result = (evt as CustomEvent).detail;
        console.log('wallet-cancelled', result)
        releaseResources();
        setLoading(false);
    };

    /**
     * PAYMENT SUCCESS.
     * ----------------
     * The result of the transaction located in the detail property.
     */
    const walletResult = (evt: Event) => {

        const result = (evt as CustomEvent).detail;
        console.log('wallet-result', result)

        const completeOrder = async () => {

            const completeOrderResult = await post('/api/bridgerpay/complete-applepay-order', {
                orderGUID,
            });

            const statusCode = completeOrderResult?.data?.relatedData?.statusCode ?? -1;

            if(statusCode === FREE_TRIAL_ABUSE_ERROR_CODE) {
                window.location.href = '/cart/trial-subscription-error';
                return;
            }

            if(statusCode === SUBSCRIPTION_ADD_PAYMENT_TOKEN_WARNING) {
                window.location.href = `/account/change-payment-method/completed?msg=1`;
                return;
            }

            if(completeOrderResult &&
                completeOrderResult.data &&
                completeOrderResult.data.relatedData &&
                completeOrderResult.data.relatedData.isValid &&
                completeOrderResult.data.relatedData.statusCode === 0) {

                redirectToThankYouPage(orderGUID, isInAppPurchase);
                return;
            }

            // Handle error ----------------------
            setServerError(DEFAULT_ERROR);
            setLoading(false);
        };

        /*
        OK result example:
        ------------------
        amount: 10
        currency: "USD"
        id: "acc5b4ce-d682-452b-b3b5-14d4cf76a6e8"
        payment_method: "wallet_apple_pay_direct"
        processing_date: "2025-08-05 11:36:40"
        psp_response: undefined
        status: "authorized"
        transaction_id: "pi_3RsjAaAHdMIPgqR72mqeNJlq"
         */

        /*
        Error result example ?
        ------------------
        amount: "49.99"
        currency: "USD"
        payment_method: "wallet_apple_pay"
        reason: "Failed to create BridgerPay transaction"
        status: "declined"
         */
        if(result?.status === 'authorized') {
            completeOrder();
        }
        else{
            const bpError = formatBridgerPayError(result?.reason || DEFAULT_ERROR);
            setServerError(bpError);
            setLoading(false);
        }

        releaseResources();
    };

    /**
     * Wallet is processing payment.
     * Triggered when a transaction is successfully processed.
     */
    const walletProcessingPayment = (evt: Event) => {
        const result = (evt as CustomEvent).detail;
        console.log('wallet-processing-payment', result)
    };

    /**
     * PSP not found in the route.
     * Should not occur on production.
     */
    const noWalletProviderFound = (evt: Event) => {
        const result = (evt as CustomEvent).detail;
        console.log('no-wallet-provider-found', result);
        releaseResources();
        setLoading(false);
    };

    /**
     * User device is not supported.
     * Unsupported device or a browser.
     */
    const walletProviderCannotBeUsed = (evt: Event) => {
        const result = (evt as CustomEvent).detail;
        console.log('wallet-provider-cannot-be-used', result);
        releaseResources();
        setLoading(false);
    };

    // Remove BridgerPay script and unregister event handlers
    // every time the ApplePay popup is requested.
    releaseResources();

    window.addEventListener(`[bp][checkout:${ cashierKey }]:wallet-button-is-ready`, walletButtonIsReady, false);
    window.addEventListener(`[bp][checkout:${ cashierKey }]:wallet-cancelled`, walletCanceled, false);
    window.addEventListener(`[bp][checkout:${ cashierKey }]:wallet-result`, walletResult, false);
    window.addEventListener(`[bp][checkout:${ cashierKey }]:wallet-processing-payment`, walletProcessingPayment, false);
    window.addEventListener(`[bp][checkout:${ cashierKey }]:no-wallet-provider-found`, noWalletProviderFound, false);
    window.addEventListener(`[bp][checkout:${ cashierKey }]:wallet-provider-cannot-be-used`, walletProviderCannotBeUsed, false);

    const script = document.createElement('script');
    const bpDomain = getHiddenDataString('bridger-pay-js-api-domain');
    script.src = `https://${ bpDomain }/v2/launcher`;
    script.id = 'bridgerpay-script';
    script.setAttribute('data-cashier-key', cashierKey);
    script.setAttribute('data-cashier-token', cashierToken);
    script.setAttribute('data-button-mode', 'wallet');
    document.body.appendChild(script);
    console.log('ApplePay BP script is added.');
};